import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { localizedPath, useCurrentLocale } from '../../lib/locale';

/**
 * Header entry point for global search (§14).
 *
 * A magnifier that expands into an input rather than a permanently visible box: the header
 * already carries the nav, the membership CTA, the language switcher and the theme toggle, and
 * a fifth full-width control would not fit at 1024px.
 *
 * Submitting navigates to /search?q= — the results page owns the query from then on. There is
 * no dropdown of suggestions here on purpose: it would need its own focus trap and keyboard
 * model, and the results page already does the job.
 */
export function SearchButton({ className = '' }: { className?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = useCurrentLocale();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const submit = () => {
    const trimmed = term.trim();
    if (!trimmed) return;
    navigate(`${localizedPath('/search', locale)}?q=${encodeURIComponent(trimmed)}`);
    setOpen(false);
    setTerm('');
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('search.label')}
        className={`inline-flex h-9 w-9 items-center justify-center rounded-pill border border-line text-content transition-colors hover:bg-content/5 ${className}`}
      >
        <SearchIcon />
      </button>
    );
  }

  return (
    <form
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      className="flex items-center"
    >
      <label htmlFor="header-search" className="sr-only">
        {t('search.label')}
      </label>
      <input
        ref={inputRef}
        id="header-search"
        type="search"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        // Escape closes; blur collapses so an abandoned input does not sit open across routes.
        onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
        onBlur={() => !term && setOpen(false)}
        placeholder={t('search.placeholder')}
        autoComplete="off"
        className="w-40 rounded-pill border border-line bg-panel px-4 py-1.5 text-sm text-content outline-none transition-colors placeholder:text-content-muted focus:border-accent sm:w-56"
      />
      {/*
        A real submit button, hidden rather than omitted. Without one, Enter relies on the
        implicit-submission rules for button-less forms — which hold only while this stays the
        single text field, and are inconsistent enough across browsers not to bet the only way
        into search on. `sr-only` keeps it reachable for screen readers and keyboard users.
      */}
      <button type="submit" className="sr-only">
        {t('search.title')}
      </button>
    </form>
  );
}

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <circle cx="8.5" cy="8.5" r="5.5" />
      <path d="M12.8 12.8 17 17" />
    </svg>
  );
}
